/**
 * Common Store - Refactored with unified architecture
 *
 * This store manages common UI state and application-wide settings.
 * Migrated to use the new unified state management patterns.
 */

import type { BaseStore } from "../core";
import { createStore, withLogging } from "../core";

// Common store state interface
interface CommonStoreState extends BaseStore {
  // UI State
  collapsed: boolean;

  // Actions
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  reset: () => void;
}

// Initial state
const initialState = {
  collapsed: false,
};

// Create the common store
export const useCommonStore = createStore<CommonStoreState>(
  withLogging("CommonStore", (set) => ({
    ...initialState,
    _initialized: false,
    _lastUpdated: Date.now(),

    // Actions
    setCollapsed: (collapsed: boolean) =>
      set((state) => {
        state.collapsed = collapsed;
        state._lastUpdated = Date.now();
      }),

    toggleCollapsed: () =>
      set((state) => {
        state.collapsed = !state.collapsed;
        state._lastUpdated = Date.now();
      }),

    reset: () =>
      set((state) => {
        Object.assign(state, initialState);
        state._initialized = false;
        state._lastUpdated = Date.now();
      }),
  })),
  {
    name: "echo-common-storage",
    persist: true,
    version: 2,
    migrate: (persistedState: unknown, version: number): CommonStoreState => {
      if (version < 2) {
        // Migrate from version 1 to 2 - add new BaseStore fields
        const oldState = persistedState as { collapsed: boolean };
        return {
          ...oldState,
          _initialized: false,
          _lastUpdated: Date.now(),
        } as CommonStoreState;
      }
      return persistedState as CommonStoreState;
    },
    partialize: (state) => ({
      collapsed: state.collapsed,
    }),
  },
);

// Selector hooks for better performance
export const useCollapsed = () => useCommonStore((state) => state.collapsed);

// Action hooks
export const useCommonActions = () =>
  useCommonStore((state) => ({
    setCollapsed: state.setCollapsed,
    toggleCollapsed: state.toggleCollapsed,
    reset: state.reset,
  }));
